/**
 * GenderFilter — utilitaire universel pour filtrer les produits par genre
 *
 * Problème résolu : même en fetchant des URL "femme", les partenaires renvoient
 * parfois des articles hommes (catégorie croisée, matching partiel, URLs ambiguës).
 *
 * Usage :
 *   import { filterByGender } from './GenderFilter';
 *   const femaleProducts = filterByGender(products, 'F');
 */

// Patterns stricts : mots clairement masculins/féminins (mots entiers)
const MALE_PATTERNS = [
  /\bmen\b/i, /\bmens\b/i, /\bman\b/i, /\bmale\b/i,
  /\bhomme\b/i, /\bhommes\b/i, /\bgentleman\b/i,
  /\bboys?\b/i, /\bgarçons?\b/i, /\bgarcons?\b/i,
  /\bpour homme\b/i, /\bfor men\b/i,
  /\bmr\./i,
];

const FEMALE_PATTERNS = [
  /\bwomen\b/i, /\bwoman\b/i, /\bfemale\b/i,
  /\bfemme\b/i, /\bfemmes\b/i, /\blady\b/i, /\bladies\b/i,
  /\bgirls?\b/i, /\bfilles?\b/i,
  /\bpour femme\b/i, /\bfor women\b/i,
  /\bmrs?\./i, /\bmiss\b/i,
];

// Brands clairement masculines (si pas de signal inverse)
const MALE_ONLY_BRANDS = [
  'carhartt wip', 'the north face homme', 'dickies', 'stone island',
];

// Brands clairement féminines
const FEMALE_ONLY_BRANDS = [
  'victoria\'s secret', 'la perla', 'chantelle', 'etam',
  'rouje', 'sezane', 'sézane', 'ba&sh', 'maje',
];

// Catégories typées par genre
const FEMALE_CATEGORIES = ['Robes', 'Sacs à main']; // normalement seulement F
// (pas de liste M, car homme peut porter n'importe quelle cat sauf Robes)

/**
 * Détecte le genre d'un article à partir de ses métadonnées texte.
 * @param {Object} product
 * @returns {'M'|'F'|null} null si ambigu
 */
export function detectProductGender(product) {
  if (!product) return null;

  const fields = [
    product.name || '',
    product.description || '',
    product.brand || '',
    product.title || '',
    // Zalando "silhouette" peut contenir "HEELS_LADIES" etc.
    product.silhouette || '',
    // Gender métadonnée si présente
    product.gender || '',
  ].join(' ').toLowerCase();

  // Métadonnée explicite → source de vérité
  if (product.gender === 'M' || product.gender === 'H') return 'M';
  if (product.gender === 'F' || product.gender === 'W') return 'F';
  if (product.gender === '*' || product.gender === 'U') return null; // unisexe

  const hasMale = MALE_PATTERNS.some(r => r.test(fields));
  const hasFemale = FEMALE_PATTERNS.some(r => r.test(fields));

  // Conflit : contient les deux → ambigu (ex: "men & women")
  if (hasMale && hasFemale) return null;
  if (hasMale) return 'M';
  if (hasFemale) return 'F';

  // Check brand
  const brand = (product.brand || '').toLowerCase();
  if (MALE_ONLY_BRANDS.some(b => brand.includes(b))) return 'M';
  if (FEMALE_ONLY_BRANDS.some(b => brand.includes(b))) return 'F';

  // Check catégorie féminine exclusive
  if (FEMALE_CATEGORIES.includes(product.category)) return 'F';

  // Check patterns féminins exclusifs dans le nom
  const femOnly = /\brobe\b|\bdress\b|\bjupe\b|\bskirt\b|\bblouse\b|\bchemisier\b|\bcrop\s*top\b|\bbustier\b|\bcombinaison\b|\btutu\b|\bleggings?\b/i;
  if (femOnly.test(fields)) return 'F';

  return null; // vraiment ambigu → laisser passer
}

/**
 * Détection STRICTE pour les Hauts — si le genre ne peut pas être confirmé
 * par un signal fort (patterns, brand exclusive, catégorie), retourne null
 * pour laisser l'appelant décider (en général : rejeter).
 */
export function detectHautsGender(product) {
  if (!product) return null;
  if (product.category !== 'Hauts') return detectProductGender(product);

  const g = detectProductGender(product);
  // Si on a un signal fort → on le retourne
  if (g !== null) return g;

  // Signal faible : on retourne 'unknown' (null) pour que l'appelant décide
  return null;
}

/**
 * Filtre une liste de produits pour ne garder que ceux qui matchent le genre demandé.
 * Les articles ambigus (unisexe, neutre) sont gardés.
 * @param {Array} products
 * @param {'M'|'F'} targetGender
 * @returns {Array}
 */
export function filterByGender(products, targetGender) {
  if (!Array.isArray(products)) return [];
  if (!targetGender || (targetGender !== 'M' && targetGender !== 'F')) return products;

  return products.filter(p => {
    const g = detectProductGender(p);
    // Garde si : même genre, OU ambigu/unisexe (null)
    if (g === null) return true;
    return g === targetGender;
  });
}

/**
 * Version plus stricte : rejette aussi les ambigus
 * Utilisée pour les images/suggestions où on veut être sûr du genre
 */
export function filterByGenderStrict(products, targetGender) {
  if (!Array.isArray(products)) return [];
  if (!targetGender || (targetGender !== 'M' && targetGender !== 'F')) return products;

  return products.filter(p => {
    const g = detectProductGender(p);
    return g === targetGender;
  });
}

/**
 * Filtre STRICT pour les Hauts : rejette ceux dont le genre ne peut pas être confirmé.
 * Raison : les Hauts (t-shirts, sweats, hoodies) sont souvent unisexes dans le texte
 * mais réellement typés dans leur coupe → on préfère rejeter les ambigus plutôt
 * que risquer d'afficher des hauts homme pour une femme.
 *
 * Les articles non-Hauts passent par le filtre standard (garde les ambigus).
 *
 * @param {Array} products
 * @param {'M'|'F'} targetGender
 */
export function filterHautsByGenderStrict(products, targetGender) {
  if (!Array.isArray(products)) return [];
  if (!targetGender || (targetGender !== 'M' && targetGender !== 'F')) return products;

  return products.filter(p => {
    const g = detectProductGender(p);
    // Si c'est un Haut : on exige un signal fort (même genre uniquement, pas d'ambigu)
    if (p.category === 'Hauts') {
      return g === targetGender;
    }
    // Autres catégories : ambigu accepté
    if (g === null) return true;
    return g === targetGender;
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FILTRE GENRE IA-FIRST — version recommandée
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Utilise en priorité `product.ai_metadata.gender` (détecté par Claude Vision
 * depuis l'image, très fiable car basé sur la coupe et le design).
 *
 * Fallback sur `detectProductGender` (regex sur texte) si :
 *   - le produit n'a pas encore été analysé par l'IA
 *   - OU l'IA n'a pas pu déterminer le genre
 *
 * L'analyse IA retourne 3 valeurs possibles :
 *   - "F"      → article féminin
 *   - "M"      → article masculin
 *   - "unisex" → article unisexe (garde pour les 2 genres)
 *
 * @param {Array} products  catalogue (de préférence enrichi via enrichCatalogWithAI)
 * @param {'M'|'F'} targetGender  genre de l'utilisateur
 * @returns {Array}
 */
export function filterByGenderAI(products, targetGender) {
  if (!Array.isArray(products)) return [];
  if (!targetGender || (targetGender !== 'M' && targetGender !== 'F')) return products;

  let usedAI = 0, usedRegex = 0;

  const filtered = products.filter(p => {
    // ═══ Priorité 1 : analyse IA ═══
    const aiGender = p?.ai_metadata?.gender;
    if (aiGender === 'F' || aiGender === 'M' || aiGender === 'unisex') {
      usedAI++;
      if (aiGender === 'unisex') return true;       // unisex passe pour les deux
      return aiGender === targetGender;
    }

    // ═══ Priorité 2 : fallback regex ═══
    usedRegex++;
    const g = detectProductGender(p);
    if (g === null) return true;                     // ambigu → on garde
    return g === targetGender;
  });

  if (usedAI + usedRegex > 0) {
    console.log(`[GenderFilter AI] ${filtered.length}/${products.length} kept · IA: ${usedAI}, regex fallback: ${usedRegex}`);
  }
  return filtered;
}

/**
 * Version IA-first STRICTE : pour les Hauts, exige un signal fort (IA ou regex).
 * Même logique que filterHautsByGenderStrict mais en priorisant l'IA.
 */
export function filterHautsByGenderStrictAI(products, targetGender) {
  if (!Array.isArray(products)) return [];
  if (!targetGender || (targetGender !== 'M' && targetGender !== 'F')) return products;

  return products.filter(p => {
    const isHaut = p?.category === 'Hauts';
    const aiGender = p?.ai_metadata?.gender;

    // Priorité IA
    if (aiGender === 'F' || aiGender === 'M') {
      if (aiGender === targetGender) return true;
      return false;
    }
    if (aiGender === 'unisex') {
      // Pour les Hauts, unisex accepté mais on pourrait être plus strict
      return !isHaut || true;
    }

    // Fallback regex
    const g = detectProductGender(p);
    if (isHaut) return g === targetGender;           // strict pour Hauts
    if (g === null) return true;                     // ambigu → on garde (hors Hauts)
    return g === targetGender;
  });
}

/**
 * Détection de la catégorie réelle d'un produit à partir de son nom.
 * Utile quand les partenaires retournent des produits mal catégorisés
 * (ex: jupe dans la catégorie "Robes", short dans "Vestes"...).
 *
 * Les regex sont en 3 niveaux de spécificité (du + précis au général).
 * On prend le PREMIER match trouvé.
 *
 * @param {string} name
 * @returns {string|null} La catégorie détectée (Robes, Hauts, Bas, Vestes, Chaussures, Sacs, Bijoux, Accessoires) ou null
 */
const CATEGORY_DETECTORS = [
  // Chaussures (ultra-spécifique d'abord)
  { cat: 'Chaussures', re: /\b(chaussures?|shoes?|sneakers?|baskets?|boots?|bottines?|bottes?|mocassins?|sandales?|escarpins?|ballerines?|tongs?|espadrilles?|tennis|runners?|pumps?|loafers?|derbys?|richelieus?|talons?|trainers?|oxfords?|brogues?|moccasin|slippers?|mules?)\b/i },
  // Sacs
  { cat: 'Sacs',       re: /\b(sacs?|bag|handbag|pochette|sacoche|cabas|backpack|totebag|crossbody|clutch|satchel|bandouli[eè]re|besace|porte[-\s]?document)\b/i },
  // Bijoux
  { cat: 'Bijoux',     re: /\b(colliers?|bracelets?|boucles?\s*d'?oreilles?|bagues?|pendentifs?|cha[iî]nettes?|cha[iî]nes?|jewelry|bijou[x]?|parure|anneau[x]?|boucle\s+oreille|ear[rg]ings?|necklace|ring|pendant)\b/i },
  // Robes (avant "Bas" car "robe-short" prioritaire à "short")
  { cat: 'Robes',      re: /\b(robes?|dresses?|dress|combinaisons?|jumpsuits?|playsuits?)\b/i },
  // v1.12.222 — Manteaux SÉPARÉS de Vestes (priorité avant Vestes)
  { cat: 'Manteaux',   re: /\b(manteaux?|trenchs?|parkas?|doudounes?|anoraks?|cabans?|peacoats?|pardessus|overcoats?|topcoats?|duffle\s*coats?|raincoats?|imperm[eé]ables?|puffers?|long\s*coats?|wool\s*coats?)\b/i },
  // Vestes (avant Hauts pour que "Veste adidas Track Top" matche Vestes
  // au lieu de Hauts via "top")
  { cat: 'Vestes',     re: /\b(vestes?|jackets?|coats?|blazers?|cardigans?|bombers?|gilets?|kimonos?|boleros?|teddys?|perfectos?|bikers?)\b/i },
  // Hauts (avant Bas pour que "Chemise Denim" matche Hauts en premier
  // au lieu de Bas via "denim")
  { cat: 'Hauts',      re: /\b(t[-\s]?shirts?|tshirts?|tops?|blouses?|chemises?|shirts?|pulls?|sweaters?|sweatshirts?|hoodies?|polos?|tuniques?|d[eé]bardeurs?|bodys?|camisoles?|crop\s*tops?|d[eé]bardeur|bustiers?|corsets?)\b/i },
  // Bas (en dernier des vêtements pour éviter les faux positifs sur "denim", "short")
  // ⚠️ NE PAS inclure "slims" : c'est un ADJECTIF de fit (Slim Fit) qu'on
  // trouve aussi dans "Chemise Slim", "Top Slim", etc. Ce serait un faux
  // positif qui rejetterait les chemises slim comme étant des Bas.
  { cat: 'Bas',        re: /\b(pantalons?|jeans?|denim|shorts?|jupes?|skirts?|leggings?|chinos?|joggings?|joggers?|bermudas?|cargos?|trousers?|pants?|bottoms?|flares?|bootcuts?|culottes?|caleçons?)\b/i },
  // Accessoires
  { cat: 'Accessoires',re: /\b(ceintures?|foulards?|echarpes?|[eé]charpes?|lunettes?|chapeaux?|casquettes?|bonnets?|gants?|montres?|cravates?|noeud[-\s]?papillon|bretelles?|parapluies?|porte[-\s]?feuille|porte[-\s]?cl[eé]s)\b/i },
];

export function detectCategoryByName(product) {
  if (!product) return null;
  const text = `${product.name || ''} ${product.title || ''} ${product.description || ''}`.toLowerCase();
  if (!text.trim()) return null;
  for (const { cat, re } of CATEGORY_DETECTORS) {
    if (re.test(text)) return cat;
  }
  return null;
}

/**
 * Filtre les produits dont la catégorie NE CORRESPOND PAS à ce qu'on voit dans leur nom.
 * Ex: un produit avec category='Robes' mais dont le nom contient "jupe" → il est rejeté.
 *
 * Règles :
 * - Si on ne peut pas détecter via le nom → on garde (on fait confiance à la catégorie)
 * - Si détection contredit la catégorie → on rejette
 * - Si la catégorie vaut 'Tout' ou est absente → on ne filtre pas
 *
 * @param {Array} products
 * @param {string} [expectedCategory] - Si fourni, ne garde que les produits dont la catégorie
 *                                       réelle (détectée ou explicite) == expectedCategory.
 */
export function filterMiscategorized(products, expectedCategory) {
  if (!Array.isArray(products)) return [];
  if (!expectedCategory || expectedCategory === 'Tout') return products;

  return products.filter(p => {
    if (!p) return false;
    const stated   = p.category;
    const detected = detectCategoryByName(p);

    // Cas 1 : détection impossible (pas de nom/description exploitable)
    //   → on garde si la catégorie déclarée matche
    if (!detected) return stated === expectedCategory;

    // Cas 2 : détection OK → on exige que la détection == expectedCategory
    //   Ça gère le cas "jupe marquée comme Robes" : detected='Bas' ≠ 'Robes' → rejet
    return detected === expectedCategory;
  });
}

/**
 * Version plus permissive : CORRIGE la catégorie si la détection contredit
 * la catégorie déclarée, plutôt que de rejeter l'article.
 *
 * Avant 2026-04 : on rejetait → 86+ articles légitimes perdus (baskets,
 *   chemises, sacs catégorisés à tort comme "Hauts" par certains partners).
 * Maintenant : on corrige la catégorie en faisant confiance à la détection
 *   nominale (plus fiable que la catégorie partner).
 *
 * Ex: produit category='Hauts', name='Baskets Merrell' → detected='Chaussures'
 *     → on REMPLACE category par 'Chaussures' (au lieu de rejeter)
 *
 * Pour conserver l'API existante, on garde le même nom de fonction. Les
 * appelants reçoivent un tableau avec des catégories CORRIGÉES.
 */
export function rejectMiscategorized(products) {
  if (!Array.isArray(products)) return [];
  let correctedCount = 0;
  const result = products.map(p => {
    if (!p || !p.category) return p;
    const detected = detectCategoryByName(p);
    if (!detected) return p; // pas de détection → on garde tel quel
    if (detected === p.category) return p; // déjà cohérent
    // Cas flexibles où on garde la catégorie d'origine (cas ambigus)
    const FLEXIBLE_PAIRS = [
      ['Hauts','Vestes'],   // pull/sweat peut être dans Vestes
      ['Accessoires','Sacs'], // pochette etc.
    ];
    const isFlexible = FLEXIBLE_PAIRS.some(([a,b]) =>
      (detected===a && p.category===b) || (detected===b && p.category===a)
    );
    if (isFlexible) return p;
    // Sinon : on CORRIGE en utilisant la catégorie détectée par le nom
    correctedCount++;
    if (correctedCount <= 5) {
      console.log(`[CategoryFilter] ✓ corrected: "${p.name}" ${p.category} → ${detected}`);
    }
    return { ...p, category: detected, _originalCategory: p.category };
  });
  if (correctedCount > 5) {
    console.log(`[CategoryFilter] … et ${correctedCount - 5} autres articles corrigés`);
  }
  if (correctedCount > 0) {
    console.log(`[CategoryFilter] total corrigés: ${correctedCount}/${products.length}`);
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════════════
// DÉTECTION & NORMALISATION DE CATÉGORIE DEPUIS LE NOM DE L'ARTICLE
// ═══════════════════════════════════════════════════════════════════════
// Les services partenaires attribuent parfois une catégorie incorrecte
// (ex: une jupe midi classée comme "Robes" chez certains partenaires).
// Ces regex permettent de vérifier/corriger en analysant le nom.
//
// L'ordre compte : on cherche d'abord les cas spécifiques (robes, vestes)
// avant les catégories génériques (hauts, bas).

const CATEGORY_REGEX = [
  // Robes & combinaisons — doit match AVANT les hauts (pas de "top robe bustier")
  { cat: 'Robes',      regex: /\b(robes?|dresses?|combinaisons?|jumpsuits?|playsuits?|tuniques?|salopettes?)\b/i },
  // v1.12.222 — Manteaux séparés de Vestes (avant Vestes pour priorité)
  { cat: 'Manteaux',   regex: /\b(manteaux?|trenchs?|parkas?|doudounes?|anoraks?|cabans?|peacoats?|pardessus|overcoats?|topcoats?|duffle\s*coats?|raincoats?|imperm[eé]ables?|long\s*coats?)\b/i },
  // Vestes — avant les hauts (sans manteaux)
  { cat: 'Vestes',     regex: /\b(vestes?|jackets?|coats?|blazers?|cardigans?|bombers?|gilets?|kimonos?|ponchos?|capes?|teddys?|perfectos?|bikers?)\b/i },
  // Chaussures — avant tout (marqueurs explicites)
  { cat: 'Chaussures', regex: /\b(chaussures?|shoes?|sneakers?|baskets?|boots?|bottines?|bottes?|mocassins?|sandales?|escarpins?|ballerines?|tongs?|espadrilles?|derbies?|mules?|sabots?)\b/i },
  // Sacs — spécifiques
  { cat: 'Sacs',       regex: /\b(sacs?|bag|bags|handbag|pochette|sacoche|cabas|backpack|totebag|bandouli[eè]re|banane)\b/i },
  // Bijoux — spécifiques
  { cat: 'Bijoux',     regex: /\b(colliers?|bracelets?|boucles? d.oreilles?|bagues?|pendentifs?|cha[iî]nettes?|cha[iî]nes?|jewelry|jewellery|bijou|boucle d.oreille)\b/i },
  // Accessoires
  { cat: 'Accessoires',regex: /\b(ceintures?|foulards?|[eé]charpes?|lunettes?|chapeaux?|casquettes?|bonnets?|gants?|montres?|cravates?|n[oœ]ud papillon|bretelles?|porte[- ]?monnaie|portefeuilles?|[eé]tuis?|porte[- ]?cl[eé]s?)\b/i },
  // Bas — pantalons, jeans, jupes, shorts
  { cat: 'Bas',        regex: /\b(pantalons?|jeans?|shorts?|jupes?|skirts?|leggings?|chinos?|joggings?|joggers?|bermudas?|cargos?|caleçons?|caleç[oô]ns?|culottes?|shorty)\b/i },
  // Hauts — en dernier (plus générique)
  { cat: 'Hauts',      regex: /\b(t-?shirts?|tshirts?|tops?|blouses?|chemises?|shirts?|pulls?|sweaters?|sweatshirts?|hoodies?|polos?|d[eé]bardeurs?|camisoles?|bustiers?|crop[- ]?tops?|tuniques?|bodies?)\b/i },
];

/**
 * Détecte la catégorie d'un article depuis son nom/description.
 * Renvoie une catégorie parmi Robes/Vestes/Chaussures/Sacs/Bijoux/Accessoires/Bas/Hauts,
 * ou null si rien ne match.
 * @param {Object} product
 * @returns {string|null}
 */
export function detectCategoryFromName(product) {
  if (!product) return null;
  const fields = [
    product.name        || '',
    product.title       || '',
    product.description || '',
    product.subcategory || '',
    product.silhouette  || '',
  ].join(' ');
  if (!fields.trim()) return null;

  for (const { cat, regex } of CATEGORY_REGEX) {
    if (regex.test(fields)) return cat;
  }
  return null;
}

/**
 * Normalise la catégorie d'un article en combinant :
 *   1. La catégorie fournie par le partenaire (si elle est dans la liste attendue)
 *   2. La détection par nom (fallback, et aussi pour corriger les erreurs)
 *
 * Si la catégorie partenaire contredit la détection par nom (ex: "Robes" mais
 * le nom contient "jupe"), on privilégie le nom (plus fiable).
 *
 * @param {Object} product
 * @returns {Object} produit avec p.category normalisée
 */
export function normalizeProductCategory(product) {
  if (!product) return product;

  const VALID_CATS = new Set(['Robes','Vestes','Chaussures','Sacs','Bijoux','Accessoires','Bas','Hauts']);

  // ═══ PRIORITÉ 1 : detectedCategory de l'IA (Claude Vision image + nom) ═══
  // C'est le signal le plus fiable : l'IA a analysé l'image ET lu le nom.
  const aiCat = product.ai_metadata?.detectedCategory;
  if (aiCat && VALID_CATS.has(aiCat)) {
    if (product.category && product.category !== aiCat) {
      console.log(`[normalizeProductCategory] IA override: "${product.name?.slice(0, 40)}" partner=${product.category} → ai=${aiCat}`);
    }
    return { ...product, category: aiCat };
  }

  // ═══ PRIORITÉ 2 : détection regex sur nom vs catégorie partenaire ═══
  const partnerCat = product.category && VALID_CATS.has(product.category) ? product.category : null;
  const nameCat = detectCategoryFromName(product);

  let finalCat = partnerCat;
  if (nameCat && partnerCat && nameCat !== partnerCat) {
    // Contradiction : privilégier nameCat sauf pour les catégories "physiques" bien typées
    const TRUST_PARTNER_FOR = new Set(['Chaussures', 'Sacs', 'Bijoux']);
    if (!TRUST_PARTNER_FOR.has(partnerCat)) {
      console.log(`[normalizeProductCategory] "${product.name?.slice(0, 50)}" : partner=${partnerCat} → name=${nameCat}`);
      finalCat = nameCat;
    }
  } else if (!partnerCat && nameCat) {
    finalCat = nameCat;
  }

  return { ...product, category: finalCat || partnerCat || product.category };
}

/**
 * Normalise une liste complète de produits.
 * @param {Array} products
 * @returns {Array}
 */
export function normalizeProductsCategories(products) {
  if (!Array.isArray(products)) return [];
  return products.map(normalizeProductCategory);
}
